import { useEffect, useRef, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";

interface NavigationState {
  pathname?: string;
  search?: string;
  hash?: string;
  isExternal?: boolean;
  href?: string;
}

interface UseProtectedTabNavigationProps {
  currentKey: string;
  editedTabs: Record<string, boolean>;
  protectedTabs: string[];
  promptWhenUnsaved: boolean;
  getModalMessage: (affectedTabKey: string | null) => string;
  clearEditedTab: (tabKey: string) => void;
}

interface UseProtectedTabNavigationReturn {
  displayedKey: string;
  showModal: boolean;
  modalMessage: string;
  handleConfirmLeave: () => void;
  handleCancelLeave: () => void;
}

export function useProtectedTabNavigation({
  currentKey,
  editedTabs,
  protectedTabs,
  promptWhenUnsaved,
  getModalMessage,
  clearEditedTab,
}: UseProtectedTabNavigationProps): UseProtectedTabNavigationReturn {
  const [showModal, setShowModal] = useState(false);
  const [pendingTabKey, setPendingTabKey] = useState<string | null>(null);
  const [pendingNavigation, setPendingNavigation] =
    useState<NavigationState | null>(null);
  const [modalMessage, setModalMessage] = useState("");
  const history = useHistory();

  const previousKeyRef = useRef(currentKey);
  const allowNavigationRef = useRef(false);
  const unblockRef = useRef<(() => void) | null>(null);

  let displayedKey = currentKey;

  if (!allowNavigationRef.current && previousKeyRef.current !== currentKey) {
    const previousKey = previousKeyRef.current;

    if (protectedTabs.includes(previousKey) && editedTabs[previousKey]) {
      displayedKey = previousKey;

      if (!showModal && !pendingTabKey) {
        Promise.resolve().then(() => {
          history.replace(`#${previousKey}`);
          setPendingTabKey(currentKey);
          setModalMessage(getModalMessage(previousKey));
          setShowModal(true);
        });
      }
    } else {
      previousKeyRef.current = currentKey;
    }
  }

  useEffect(() => {
    if (allowNavigationRef.current && currentKey === previousKeyRef.current) {
      allowNavigationRef.current = false;
    }
  }, [currentKey]);

  useEffect(() => {
    if (!promptWhenUnsaved) {
      return undefined;
    }

    const currentTabIsProtectedAndEdited =
      protectedTabs.includes(currentKey) && editedTabs[currentKey];

    if (!currentTabIsProtectedAndEdited) {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      return undefined;
    }

    unblockRef.current = history.block((location) => {
      if (location.pathname === history.location.pathname) {
        return undefined;
      }

      setPendingNavigation({
        pathname: location.pathname,
        search: location.search || "",
        hash: location.hash || "",
      });
      setModalMessage(getModalMessage(currentKey));
      setShowModal(true);

      return false;
    });

    const handleLinkClick = (e: MouseEvent) => {
      const { target } = e;
      const link = (target as HTMLElement).closest("a");

      if (link && link.href) {
        const currentOrigin = window.location.origin;
        const currentPath = window.location.pathname;

        let linkUrl: URL;
        try {
          linkUrl = new URL(link.href, currentOrigin);
        } catch {
          return;
        }

        const isHashOnly = linkUrl.pathname === currentPath && linkUrl.hash;

        if (isHashOnly) {
          return;
        }

        const isDifferentOrigin = linkUrl.origin !== currentOrigin;
        const isDifferentPath = linkUrl.pathname !== currentPath;
        const isExternalNavigation =
          isDifferentOrigin || link.target === "_blank";

        if (
          (isExternalNavigation || isDifferentPath) &&
          !link.getAttribute("data-react-router-link")
        ) {
          e.preventDefault();
          e.stopPropagation();

          setPendingNavigation({
            isExternal: link.target === "_blank",
            href: link.href,
          });
          setModalMessage(getModalMessage(currentKey));
          setShowModal(true);
        }
      }
    };

    document.addEventListener("click", handleLinkClick, true);

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, [
    history,
    currentKey,
    editedTabs,
    promptWhenUnsaved,
    getModalMessage,
    protectedTabs,
  ]);

  const handleConfirmLeave = useCallback(() => {
    if (pendingTabKey && previousKeyRef.current) {
      clearEditedTab(previousKeyRef.current);
    }

    if (pendingTabKey) {
      allowNavigationRef.current = true;
      previousKeyRef.current = pendingTabKey;
      history.push(`#${pendingTabKey}`);
      setShowModal(false);
      setPendingTabKey(null);
    }

    if (pendingNavigation) {
      const nav = pendingNavigation;

      if (currentKey) {
        clearEditedTab(currentKey);
      }

      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }

      if (nav.isExternal) {
        window.open(nav.href, "_blank", "noopener,noreferrer");
      } else if (nav.href) {
        window.location.href = nav.href;
      } else {
        const pathname = nav.pathname ?? "";
        const search = nav.search ?? "";
        const hash = nav.hash ?? "";
        history.push(pathname + search + hash);
      }

      setPendingNavigation(null);
    }

    setShowModal(false);
  }, [pendingTabKey, pendingNavigation, history, clearEditedTab, currentKey]);

  const handleCancelLeave = useCallback(() => {
    setShowModal(false);
    setPendingTabKey(null);
    setPendingNavigation(null);
  }, []);

  return {
    displayedKey,
    showModal,
    modalMessage,
    handleConfirmLeave,
    handleCancelLeave,
  };
}
